// Import data — bring a chart of accounts across from Sage / Xero / QuickBooks / Pastel.
//
// Entry point (decided 2026-07-23): this is NOT part of first-company onboarding. A company is
// always created fresh with its seeded chart, billing is activated, and THEN we offer the import
// — from the billing-return screen and from a permanent "Import data" action on the company card.
// So by the time this screen runs, the company and its seeded chart already exist, which is
// exactly what the mapping design assumes: an import MAPS ONTO the seeded chart, it never
// replaces it.
//
// Three phases in ONE template, switched with ?hidden — never by swapping reactive blocks. That
// keeps the <input type=file> and the source picker in the STATIC template, so a re-render (an
// error message, a busy flag) can never wipe the file the user just chose.
//
// The mapping table is the one place inputs live inside a reactive slot, because the rows only
// exist after the upload. It re-renders ONLY when `version` bumps (load / save) — per-row edits
// write into the plain `job.mappings` objects and deliberately bump nothing, so typing in a
// target-code box never re-renders the grid out from under the caret.
import { html, signal, navigate } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { logout } from '../shared/auth.js';
import { theme, toggleTheme } from '../shared/theme.js';
import { icon } from '../shared/icons.js';
import { bottomNav, tenantNavItems } from '../shared/bottomnav.js';
import { demoBadge } from '../shared/demo.js';
import { t, initI18n } from '../shared/i18n.js';

// import.source_system, minus the values a user would never pick by hand.
export const SOURCES = [
  { value: 'xero', label: 'Xero' },
  { value: 'sage50', label: 'Sage 50' },
  { value: 'sage_one', label: 'Sage One / Sage Business Cloud' },
  { value: 'quickbooks', label: 'QuickBooks' },
  { value: 'pastel', label: 'Pastel' },
  { value: 'csv', label: 'Other / plain CSV' },
];

// import.mapping.action — the CHECK constraint allows exactly these three.
export const ACTIONS = [
  { value: 'create', label: 'Create new' },
  { value: 'map', label: 'Map onto existing' },
  { value: 'skip', label: 'Skip' },
];

export const actionLabel = value => (ACTIONS.find(a => a.value === value) || {}).label || value || '';

// What the row's second column should say about WHY this action was proposed.
export function reasonHint(mapping){
  if(mapping.reason === 'code_match') return mapping.name_conflict
    ? 'Same code as an existing account, but the names differ — check this one'
    : 'Matches an existing account by code';
  if(mapping.reason === 'name_match') return 'Matches an existing account by name';
  if(mapping.reason === 'invalid_code') return 'No valid 4-character code — give it one, or skip it';
  return 'New account';
}

// `isDemo` is a SIGNAL, not a boolean: the company is resolved from /api/me after the first
// render, so the badge has to be able to appear when the answer arrives.
function shell(title, body, companyId, isDemo){
  const signOut = () => { logout(); navigate('/login'); };
  return html`
    <div class="main tenant-main">
      <header class="app-bar">
        <div class="brand-lockup"><span class="brand-dot"></span><span class="brand-name">aboo</span></div>
        <div class="app-title">${title}</div>
        ${() => isDemo && isDemo.value ? demoBadge() : null}
        <div class="app-user">
          <button class="theme-toggle" title=${t('common.toggle_theme', 'Toggle light / dark')} @click=${() => toggleTheme()}>${() => theme.value === 'dark' ? icon('sun', {size: 18}) : icon('moon', {size: 18})}</button>
          <button class="btn btn-sm btn-outline" @click=${() => navigate('/home')}>${() => t('common.home', 'Home')}</button>
        </div>
      </header>
      <div class="content">${body}</div>
      ${bottomNav(tenantNavItems({ companyId, onSignOut: signOut }), 'import')}
    </div>`;
}

export function importScreen(companyId){
  initI18n(companyId);                       // own shell (no tenantShell) → load the company's language bundle here
  const phase = signal('upload');            // upload | mapping | done
  const busy = signal(false);
  const err = signal('');
  const rowErrors = signal([]);              // per-row reasons a commit was refused
  const companyName = signal('');
  const isDemo = signal(false);              // drives the DEMO badge in the app chrome
  const chosenName = signal('');
  const source = signal('xero');
  const version = signal(0);                 // bumps ONLY when the grid must be rebuilt
  const result = signal(null);

  // Plain (non-signal) state: mutated by row inputs without triggering a re-render.
  let job = null;
  let chosenFile = null;
  let categories = [];

  (async () => {
    try {
      const me = await api.get('/me');
      const company = (me.companies || []).find(c => String(c.id) === String(companyId));
      companyName.value = company ? company.name : '';
      isDemo.value = !!(company && company.is_demo);
      if(!company) err.value = t('import.err_not_yours', 'That company is not on your account.');
    } catch (e){ err.value = (e.data && e.data.error) || e.message || t('import.err_load_account', 'Could not load your account'); }
  })();

  (async () => {
    try {
      // The company's own categories (roots left out): its accounting standard's list — the IFRS list, or its
      // country's GAAP list — which is what the import commit matches a category code against. In the order the
      // admin list keeps (sort order, then code).
      const r = await api.get('/companies/' + companyId + '/account-categories');
      categories = (r.data || []).slice().sort((a, b) => ((a.sort_order ?? 0) - (b.sort_order ?? 0))
        || String(a.code).localeCompare(String(b.code)));
      version.value++;
    } catch { categories = []; }
  })();

  function onFile(e){
    chosenFile = (e.target.files && e.target.files[0]) || null;
    chosenName.value = chosenFile ? chosenFile.name : '';
    err.value = '';
  }

  async function doUpload(e){
    if(e) e.preventDefault();
    if(!chosenFile){ err.value = t('import.err_choose_file', 'Choose a CSV file to import.'); return; }
    busy.value = true; err.value = ''; rowErrors.value = [];
    try {
      const form = new FormData();
      form.append('file', chosenFile, chosenFile.name);
      form.append('source_system', source.value);
      form.append('kind', 'chart_of_accounts');
      job = await api.upload(`/companies/${companyId}/imports`, form);
      version.value++;
      phase.value = 'mapping';
    } catch (e2){
      err.value = (e2.data && e2.data.error) || e2.message || t('import.err_read', 'Could not read that file');
    }
    busy.value = false;
  }

  async function doCommit(){
    if(!job) return;
    busy.value = true; err.value = ''; rowErrors.value = [];
    try {
      const mappings = job.mappings.map(m => ({
        source_code: m.source_code, action: m.action,
        target_code: m.target_code || null, category_code: m.category_code || null,
      }));
      job = await api.patch(`/companies/${companyId}/imports/${job.id}/mappings`, { mappings });
      result.value = await api.post(`/companies/${companyId}/imports/${job.id}/commit`, {});
      phase.value = 'done';
    } catch (e2){
      err.value = (e2.data && e2.data.error) || e2.message || t('import.err_commit', 'Could not import these accounts');
      rowErrors.value = (e2.data && e2.data.rows) || [];
      version.value++;
    }
    busy.value = false;
  }

  function startOver(){
    job = null; chosenFile = null; chosenName.value = ''; result.value = null;
    err.value = ''; rowErrors.value = []; phase.value = 'upload';
  }

  // One row of the mapping grid. Handlers write into the plain mapping object — no signal is
  // touched, so the grid does not re-render and the caret stays where the user put it.
  const mappingRow = m => html`
    <div class=${'row-card import-row' + (m.name_conflict ? ' row-warn' : '')}>
      <span class="row-main">
        <b>${m.source_code}</b>
        <span class="row-sub">${m.source_name || '(no name)'}${m.source_type ? ' · ' + m.source_type : ''}</span>
        <span class="row-sub muted">${reasonHint(m)}</span>
      </span>
      <label class="visually-hidden" for=${'act-' + m.source_code}>Action</label>
      <select class="form-control import-action" id=${'act-' + m.source_code}
              @change=${e => { m.action = e.target.value; }}>
        ${ACTIONS.map(a => html`<option value=${a.value} ?selected=${a.value === m.action}>${a.label}</option>`)}
      </select>
      <label class="visually-hidden" for=${'tgt-' + m.source_code}>Account code</label>
      <input class="form-control import-target" id=${'tgt-' + m.source_code}
             value=${m.target_code || ''} placeholder="e.g. 8400" maxlength="4"
             @input=${e => { m.target_code = e.target.value; }}>
      <label class="visually-hidden" for=${'cat-' + m.source_code}>${t('common.category', 'Category')}</label>
      <select class="form-control import-category" id=${'cat-' + m.source_code}
              @change=${e => { m.category_code = e.target.value; }}>
        <option value="">${t('import.category_placeholder', '— Select a category —')}</option>
        ${categories.map(c => html`<option value=${c.code} ?selected=${c.code === m.category_code}>${c.name}</option>`)}
      </select>
    </div>`;

  // Reactive slot #1 — the grid. Reads `version` (and nothing a keystroke can change) at the top,
  // then uses plain values only: a nested ${() => …} inside here would append on re-render.
  const grid = () => {
    version.value;
    if(!job) return html`<div class="muted-block">${t('import.nothing_loaded', 'Nothing loaded yet.')}</div>`;
    const counts = (job.summary && job.summary.counts) || {};
    const warnings = counts.warnings || [];
    return html`
      <div class="import-counts">
        <span class="badge badge-light">${counts.total || 0} rows</span>
        <span class="badge badge-success">${counts.create || 0} to create</span>
        <span class="badge badge-info">${counts.map || 0} to map onto existing</span>
        <span class="badge badge-light">${counts.skip || 0} skipped</span>
      </div>
      <div class="form-note">${t('import.grid_note', 'Your chart of accounts is already set up — this maps your old accounts onto it. Anything marked "Create new" is added; "Map onto existing" just records the match and changes nothing; "Skip" is ignored.')}</div>
      ${warnings.map(w => html`<div class="alert alert-warning py-2 mb-2">${w}</div>`)}
      <div class="row-list import-rows">${job.mappings.map(mappingRow)}</div>`;
  };

  const rowErrorList = () => {
    const rows = rowErrors.value;
    if(!rows.length) return null;
    return html`<div class="alert alert-danger py-2 mb-2">
      <div>${t('import.errors_intro', 'Nothing was imported. Fix these rows and try again:')}</div>
      <ul class="import-errors">${rows.map(r => html`<li>${r}</li>`)}</ul>
    </div>`;
  };

  const doneBody = () => {
    const r = result.value || {};
    return html`
      <h2 class="onboard-title">${t('import.done_title', 'Import complete ✓')}</h2>
      <p class="login-hint mb-3">
        ${r.created || 0} account${r.created === 1 ? '' : 's'} created,
        ${r.mapped || 0} mapped onto your existing chart,
        ${r.skipped || 0} skipped.
      </p>`;
  };

  const content = html`
    <div class="page">
      <div class="page-head with-action">
        <div>
          <h1 class="page-title">${() => t('common.import_data', 'Import data')}</h1>
          <div class="page-sub">${() => companyName.value || t('common.your_company', 'Your company')}</div>
        </div>
        <!-- Mid-mapping you need to know what already exists to map ONTO — so the chart is one
             tap away from here, not back through Home. -->
        <button class="btn btn-outline" title=${t('import.chart_hint', 'See the accounts this company already has')}
                @click=${() => navigate('/companies/' + companyId + '/accounts')}>${() => t('coa.title', 'Chart of accounts')}</button>
      </div>

      <div class="import-phase" ?hidden=${() => phase.value !== 'upload'}>
        <div class="form-note mb-2">${() => t('import.upload_note', "Export your chart of accounts as a CSV from your old system and upload it here. We'll show you exactly what will happen before anything is changed.")}</div>
        <label class="form-label" for="import-source">${() => t('import.source_label', 'Where is it from?')}</label>
        <select class="form-control mb-2" id="import-source" .value=${source}
                @change=${e => { source.value = e.target.value; }}>
          ${SOURCES.map(s => html`<option value=${s.value}>${s.label}</option>`)}
        </select>
        <label class="form-label" for="import-file">${() => t('import.file_label', 'CSV file')}</label>
        <input class="form-control mb-2" id="import-file" type="file" accept=".csv,.txt,.tsv"
               placeholder="e.g. chart-of-accounts.csv" @change=${onFile}>
        <div class="form-note">${() => chosenName.value ? t('import.selected', 'Selected:') + ' ' + chosenName.value : t('import.no_file', 'No file chosen yet.')}</div>
        <div class="form-actions">
          <button type="button" class="btn btn-outline"
                  @click=${() => navigate('/home')}>${t('common.cancel', 'Cancel')}</button>
          <span class="form-actions-spacer"></span>
          <button type="button" class="btn btn-primary" ?disabled=${() => busy.value}
                  @click=${doUpload}>${() => busy.value ? t('import.reading', 'Reading…') : t('import.upload_preview', 'Upload and preview')}</button>
        </div>
      </div>

      <div class="import-phase" ?hidden=${() => phase.value !== 'mapping'}>
        ${grid}
        ${rowErrorList}
        <div class="form-actions">
          <button type="button" class="btn btn-outline"
                  @click=${() => navigate('/home')}>${t('common.cancel', 'Cancel')}</button>
          <button type="button" class="btn btn-outline" @click=${startOver}>${() => t('import.choose_another', 'Choose another file')}</button>
          <span class="form-actions-spacer"></span>
          <button type="button" class="btn btn-primary" ?disabled=${() => busy.value}
                  @click=${doCommit}>${() => busy.value ? t('import.importing', 'Importing…') : t('import.commit_button', 'Import these accounts')}</button>
        </div>
        <div class="form-note">${() => t('import.commit_note', 'Nothing is written to your books until you press "Import these accounts" — leaving now changes nothing.')}</div>
      </div>

      <div class="import-phase" ?hidden=${() => phase.value !== 'done'}>
        ${doneBody}
        <div class="form-actions">
          <button type="button" class="btn btn-outline" @click=${startOver}>${() => t('import.import_another', 'Import another file')}</button>
          <span class="form-actions-spacer"></span>
          <button type="button" class="btn btn-primary" @click=${() => navigate('/home')}>${() => t('common.back_home', 'Back to home')}</button>
        </div>
      </div>

      ${() => err.value ? html`<div class="alert alert-danger py-2 mb-2">${err.value}</div>` : null}
    </div>`;

  return shell(() => t('common.import_data', 'Import data'), content, companyId, isDemo);
}
